import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, ShieldCheck } from 'lucide-react';
import { apiFetch, toast } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
        silent: true,
      });

      const data = await response.clone().json().catch(() => ({}));
      if (!response.ok) {
        setError(data.detail || 'Failed to change password.');
        return;
      }

      toast.success('Password updated successfully. Please use your new password next time.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => navigate('/dashboard'), 800);
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="grid-background absolute inset-0 opacity-20"></div>

      <div className="relative z-10 w-full max-w-md">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-[#00f0ff] hover:text-[#ff00ff] transition-colors mb-6"
          data-testid="change-password-back-button"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="text-center mb-8">
          <div className="text-6xl glow mb-4">
            <ShieldCheck className="inline text-[#00f0ff]" size={72} />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text mb-2">
            Change Password
          </h1>
          <p className="text-[#00f0ff]/70">Rotate your credentials to keep your account secure</p>
        </div>

        <div className="glass rounded-2xl p-8 glow-box">
          {error && (
            <div
              className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400"
              data-testid="change-password-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00f0ff]/50" size={20} />
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#ff00ff]/50" size={20} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#ff00ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none transition-all"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#ff00ff]/50" size={20} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#ff00ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none transition-all"
                  placeholder="Re-enter new password"
                  required
                  minLength={8}
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-primary text-white font-bold rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="change-password-submit-button"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <span>Update Password</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
